import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MainContainer from "../../layout/MainContainer";
import "../../pages/users/Users.scss";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  useChangeDocumentStatusMutation,
  useLazyGetAgentRequestsQuery,
  useLazyGetUserByIdQuery,
  useExpireUserSubscriptionMutation,
  useCancelUserSubscriptionMutation,
} from "../../services/users";
import { AllUser, CommonBody } from "../../types/General";
import { showError, showToast } from "../../constants";
import useTranslation from "../../hooks/Translation";
import moment from "moment";
import { resolveMediaUrl, isPdfUrl } from "../../utils/mediaUrl";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import NoDataFound from "../../components/NoDataFound";
import { formatPhoneLtr } from "../../utils/phoneDisplay";
import DemoToolsPanel from "../../components/DemoToolsPanel";

const subscriptionStatusLabel = (status?: number) => {
  switch (Number(status)) {
    case 0:
      return "Cancelled";
    case 1:
      return "Active";
    case 2:
      return "Expired";
    default:
      return "-";
  }
};

const subscriptionStatusColor = (
  status?: number,
): "success" | "warning" | "error" | "default" => {
  switch (Number(status)) {
    case 0:
      return "error";
    case 1:
      return "success";
    case 2:
      return "warning";
    default:
      return "default";
  }
};

const paymentTypeLabel = (type?: number) => {
  switch (Number(type)) {
    case 1:
      return "Wallet Top-up";
    case 2:
      return "Subscription";
    case 3:
      return "Request to Agent";
    default:
      return "-";
  }
};

const paymentStatusLabel = (status?: number | string) => {
  switch (Number(status)) {
    case 1:
      return "Pending";
    case 2:
      return "Completed";
    case 3:
      return "Cancelled";
    case 4:
      return "Refunded";
    default:
      return status || "-";
  }
};

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function CustomTabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const AgentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [getUserData] = useLazyGetUserByIdQuery();
  const [agentRequests] = useLazyGetAgentRequestsQuery();
  const [changeDocumentStatus] = useChangeDocumentStatusMutation();
  const [expireUserSubscription] = useExpireUserSubscriptionMutation();
  const [cancelUserSubscription] = useCancelUserSubscriptionMutation();
  const [useDetails, setUserDetails] = useState<any>();
  const [requestsData, setRequestsData] = useState<any>();

  console.log(requestsData, "requestsData");

  const translate = useTranslation();
  const [value, setValue] = React.useState(0);
  const [licensePreviewOpen, setLicensePreviewOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planActionDialog, setPlanActionDialog] = useState<{
    open: boolean;
    action: "expire" | "cancel" | null;
    plan: any;
  }>({ open: false, action: null, plan: null });
  const [planActionLoading, setPlanActionLoading] = useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const getUserDetailById = async (id: string | undefined) => {
    try {
      const response = await getUserData({ user_id: id }).unwrap();
      if (response?.statusCode === 200) {
        setUserDetails(response?.data);
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const getAgentsRequestsApi = async (id: string | undefined) => {
    try {
      const response = await agentRequests({ user_id: id }).unwrap();
      if (response?.statusCode === 200) {
        setRequestsData(response?.data);
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const handleDocumentStatus = async (status: 2 | 3, reason?: string) => {
    setIsSubmitting(true);
    try {
      const body: any = { documentStatus: status };
      if (status === 3 && reason) {
        body.userRejectedReason = reason;
      }
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await changeDocumentStatus({ userId: id!, encryptedBody }).unwrap();
      if (response?.statusCode === 200) {
        showToast(status === 2 ? "Document accepted successfully" : "Document rejected successfully");
        setRejectDialogOpen(false);
        setRejectReason("");
        getUserDetailById(id);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlanActionConfirm = async () => {
    if (!id || !planActionDialog.plan?._id || !planActionDialog.action) return;
    setPlanActionLoading(true);
    try {
      const payload = {
        userId: id,
        subscriptionId: String(planActionDialog.plan._id),
      };
      const response =
        planActionDialog.action === "expire"
          ? await expireUserSubscription(payload).unwrap()
          : await cancelUserSubscription(payload).unwrap();
      if (response?.statusCode === 200) {
        showToast(
          planActionDialog.action === "expire"
            ? "Plan expired successfully"
            : "Plan cancelled successfully",
        );
        setPlanActionDialog({ open: false, action: null, plan: null });
        getUserDetailById(id);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Something went wrong");
    } finally {
      setPlanActionLoading(false);
    }
  };

  useEffect(() => {
    getUserDetailById(id);
    getAgentsRequestsApi(id);
  }, []);
  console.log("acceptRequests", useDetails?.subscriptionPlan?.acceptRequests);

  // Calculate totals from subscriptionPlan array
  const subscriptionPlans = Array.isArray(useDetails?.subscriptionPlan)
    ? useDetails?.subscriptionPlan
    : useDetails?.subscriptionPlan?.planName
      ? [useDetails?.subscriptionPlan]
      : [];

  // Use direct API fields for credits bought
  const totalNormalCreditsBought = useDetails?.normalCreditBoughtCount || 0;
  const totalHighlightCreditsBought = useDetails?.highlightCreditBoughtCount || 0;

  /**
   * Plan-wise credit math: each ACTIVE plan keeps its own allotment.
   * Display Plan = Used + Remaining (never negative "used").
   */
  const creditBreakdown = (granted: number | undefined, left: number | undefined) => {
    const base = Number(granted) || 0;
    const remaining = Number(left) || 0;
    const used = Math.max(0, base - remaining);
    const planTotal = remaining + used;
    const carried = Math.max(0, remaining - base);
    return { base, planTotal, used, remaining, carried };
  };

  const sumBreakdown = (plans: any[], grantedKey: string, leftKey: string) =>
    plans.reduce(
      (acc, plan) => {
        const b = creditBreakdown(plan?.[grantedKey], plan?.[leftKey]);
        acc.planTotal += b.planTotal;
        acc.used += b.used;
        acc.remaining += b.remaining;
        acc.carried += b.carried;
        return acc;
      },
      { planTotal: 0, used: 0, remaining: 0, carried: 0 },
    );

  const requestsTotals = sumBreakdown(subscriptionPlans, "acceptRequests", "acceptRequestsLeft");
  const normalTotals = sumBreakdown(subscriptionPlans, "normalCredit", "normalCreditLeft");
  const highlightTotals = sumBreakdown(subscriptionPlans, "highlightCredit", "highlightCreditLeft");

  const totalRequests = requestsTotals.planTotal;
  const totalRequestsLeft = requestsTotals.remaining;
  const totalRequestsUsed = requestsTotals.used;

  const totalNormalCredits = normalTotals.planTotal;
  const totalNormalCreditsLeft = normalTotals.remaining;
  const totalNormalCreditsUsed = normalTotals.used;

  const totalHighlightCredits = highlightTotals.planTotal;
  const totalHighlightCreditsLeft = highlightTotals.remaining;
  const totalHighlightCreditsUsed = highlightTotals.used;

  const licenseUrl = resolveMediaUrl(useDetails?.licenseDocument);
  const licensePreview = licenseUrl
    ? isPdfUrl(licenseUrl)
      ? "/static/images/pdf.png"
      : licenseUrl
    : "/static/images/pdf.png";

  const handleViewLicense = () => {
    if (licenseUrl) setLicensePreviewOpen(true);
  };

  const getValidityLabel = (
    validity?: number,
    validityUnit?: string
  ) => {
    if (!validity) return "N/A";
    const isOne = validity === 1;
    if (validityUnit === "minutes") {
      return `${validity} ${
        isOne
          ? translate?.main?.Minute || "Minute"
          : translate?.main?.Minutes || "Minutes"
      }`;
    }
    if (validityUnit === "hours") {
      return `${validity} ${
        isOne
          ? translate?.main?.Hour || "Hour"
          : translate?.main?.Hours || "Hours"
      }`;
    }
    if (validityUnit === "days") {
      return `${validity} ${
        isOne
          ? translate?.main?.Day || "Day"
          : translate?.main?.Days || "Days"
      }`;
    }
    return `${validity} ${
      isOne ? translate?.main?.Month : translate?.main?.Months
    }`;
  };

  return (
    <MainContainer>
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.agents?.ViewAgentProfile}</h1>
          <Button
            className="btn btn_primary"
            onClick={() => {
              navigate("/manage-agents");
            }}
          >
            {translate?.main?.Back}
          </Button>
        </div>
        <Card className="cards">
          <Box className="custom_tabs">
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label={translate?.agents?.profile} {...a11yProps(0)} />
              <Tab label={translate?.agents?.licenseDocuments} {...a11yProps(1)} />
            </Tabs>

            <CustomTabPanel value={value} index={0}>
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2} className="view_box">
                  <Grid item xs={10} className="view_box_list">
                    <Grid
                      item
                      lg={2}
                      md={2}
                      sm={6}
                      xs={12}
                      sx={{ paddingBottom: 5 }}
                    >
                      <figure className="profile_img">
                        <img
                          src={
                            useDetails?.image
                              ? useDetails?.image
                              : "/static/images/user_placeholder.png"
                          }
                          alt=""
                        />
                      </figure>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {translate?.main?.Name}
                          </Typography>
                          <Typography component="p">
                            {useDetails?.fullName || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {translate?.main?.PhoneNo}
                          </Typography>
                          <Typography component="p">
                            {formatPhoneLtr(
                              useDetails?.countryCode,
                              useDetails?.phone
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {translate?.main?.Email}
                          </Typography>
                          <Typography component="p">
                            {useDetails?.email || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {translate?.main?.AcountStatus}
                          </Typography>
                          <Typography component="p">
                            {useDetails?.isBlocked == false
                              ? translate?.main?.active
                              : translate?.main?.Inactive}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {translate?.agents?.normalCreditsBought}
                          </Typography>
                          <Typography component="p">
                            {totalNormalCreditsBought || 0}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {translate?.agents?.highlightedCreditsBought}
                          </Typography>
                          <Typography component="p">
                            {totalHighlightCreditsBought || 0}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={3} md={3} sm={6} xs={12} />
                      <Grid item lg={3} md={3} sm={6} xs={12} />

                      {subscriptionPlans.length > 0 ? (
                        subscriptionPlans.map((plan: any, planIndex: number) => {
                          const requests = creditBreakdown(
                            plan?.acceptRequests,
                            plan?.acceptRequestsLeft,
                          );
                          const normal = creditBreakdown(
                            plan?.normalCredit,
                            plan?.normalCreditLeft,
                          );
                          const highlight = creditBreakdown(
                            plan?.highlightCredit,
                            plan?.highlightCreditLeft,
                          );
                          return (
                          <React.Fragment key={plan?._id || planIndex}>
                            <Grid item mt={3} xs={12} className="view_box_list">
                              <Typography
                                className="detail_title mn_hdng"
                                component="h2"
                                mb={3}
                              >
                                {translate?.main?.PlanDetails} {subscriptionPlans.length > 1 ? `(${planIndex + 1})` : ""}
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">
                                      {translate?.main?.PlanName}
                                    </Typography>
                                    <Typography component="p">
                                      {plan?.planName || "-"}
                                    </Typography>
                                  </Box>
                                </Grid>

                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">
                                      {translate?.main?.PlanPrice}
                                    </Typography>
                                    <Typography component="p">
                                      {plan?.price || "-"}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">
                                      {translate?.main?.PurchaseDate}
                                    </Typography>
                                    <Typography component="p">
                                      {plan?.createdAt
                                        ? moment(plan?.createdAt).format("DD-MM-YYYY")
                                        : "-"}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">
                                      {translate?.main?.ExpiryDate}
                                    </Typography>
                                    <Typography component="p">
                                      {plan?.expiryDate
                                        ? moment(plan?.expiryDate).format("DD-MM-YYYY")
                                        : "-"}
                                    </Typography>
                                  </Box>
                                </Grid>

                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">
                                      {translate?.main?.SubscriptionType}
                                    </Typography>
                                    <Typography component="p">
                                      {getValidityLabel(plan?.validity, plan?.validityUnit)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
                                    <Button
                                      variant="outlined"
                                      color="warning"
                                      size="small"
                                      disabled={!plan?._id || planActionLoading}
                                      onClick={() =>
                                        setPlanActionDialog({
                                          open: true,
                                          action: "expire",
                                          plan,
                                        })
                                      }
                                    >
                                      Expire Plan
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      disabled={!plan?._id || planActionLoading}
                                      onClick={() =>
                                        setPlanActionDialog({
                                          open: true,
                                          action: "cancel",
                                          plan,
                                        })
                                      }
                                    >
                                      Cancel Plan
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item mt={3} xs={12} className="view_box_list">
                              <Typography
                                className="detail_title mn_hdng"
                                component="h2"
                                mb={3}
                              >
                                {translate?.agents?.planSubscriptionDetails} {subscriptionPlans.length > 1 ? `(${planIndex + 1})` : ""}
                              </Typography>

                              <Grid container spacing={3}>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">{translate?.agents?.request}</Typography>
                                    <Typography component="p">
                                      {requests.planTotal}
                                      {requests.carried > 0
                                        ? ` (${requests.base} + ${requests.carried} carried)`
                                        : ""}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">
                                      {translate.main.NormalCredits}
                                    </Typography>
                                    <Typography component="p">
                                      {normal.planTotal}
                                      {normal.carried > 0
                                        ? ` (${normal.base} + ${normal.carried} carried)`
                                        : ""}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography component="h5">
                                      {translate.main.HighlightedCredits}
                                    </Typography>
                                    <Typography component="p">
                                      {highlight.planTotal}
                                      {highlight.carried > 0
                                        ? ` (${highlight.base} + ${highlight.carried} carried)`
                                        : ""}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Totals for this plan */}
                            <Grid item mt={3} xs={12}>
                              <Grid container spacing={3}>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography
                                      component="h3"
                                      className="detail_title mn_hdng"
                                    >
                                      {translate?.agents?.totalRequestsPlan}
                                    </Typography>
                                    <Typography component="p">
                                      {requests.planTotal}
                                    </Typography>
                                    {requests.carried > 0 ? (
                                      <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                                        Base {requests.base} + {requests.carried} carried
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography
                                      component="h3"
                                      className="detail_title mn_hdng"
                                    >
                                      {translate?.agents?.requestsUsed}
                                    </Typography>
                                    <Typography component="p">
                                      {requests.used}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box>
                                    <Typography
                                      component="h3"
                                      className="detail_title mn_hdng"
                                    >
                                      {translate?.agents?.remainingRequests}
                                    </Typography>
                                    <Typography component="p">
                                      {requests.remaining}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Normal Listing Credits for this plan */}
                            <Grid item mt={3} xs={12} className="view_box_list">
                              <Typography
                                className="detail_title mn_hdng"
                                component="h2"
                                mb={3}
                              >
                                {translate?.main?.NormalListingCredits} {subscriptionPlans.length > 1 ? `(${planIndex + 1})` : ""}
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box mt={2}>
                                    <Typography component="h5">
                                      {translate?.agents?.normalCreditsPlan}
                                    </Typography>
                                    <Typography component="p">
                                      {normal.planTotal}
                                    </Typography>
                                    {normal.carried > 0 ? (
                                      <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                                        Base {normal.base} + {normal.carried} carried
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Grid>
                                <Grid item xs={3}>
                                  <Box mt={2}>
                                    <Typography component="h5">
                                      {translate?.agents?.normalCreditsUsed}
                                    </Typography>
                                    <Typography component="p">
                                      {normal.used}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box mt={2}>
                                    <Typography component="h5">
                                      {translate?.agents?.normalCreditsAvailable}
                                    </Typography>
                                    <Typography component="p">
                                      {normal.remaining}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Highlighted Listing Credits for this plan */}
                            <Grid item mt={3} xs={12} className="view_box_list">
                              <Typography
                                className="detail_title mn_hdng"
                                component="h2"
                                mb={3}
                              >
                                {translate?.main?.HighlightedListingCredits} {subscriptionPlans.length > 1 ? `(${planIndex + 1})` : ""}
                              </Typography>
                              <Grid container spacing={3}>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box mt={2}>
                                    <Typography component="h5">
                                      {translate?.agents?.highlightCreditsPlan}
                                    </Typography>
                                    <Typography component="p">
                                      {highlight.planTotal}
                                    </Typography>
                                    {highlight.carried > 0 ? (
                                      <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                                        Base {highlight.base} + {highlight.carried} carried
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Grid>
                                <Grid item xs={3}>
                                  <Box mt={2}>
                                    <Typography component="h5">
                                      {translate?.agents?.highlightsCreditsUsed}
                                    </Typography>
                                    <Typography component="p">
                                      {highlight.used}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                  <Box mt={2}>
                                    <Typography component="h5">
                                      {translate?.agents?.highlightsCreditsAvailable}
                                    </Typography>
                                    <Typography component="p">
                                      {highlight.remaining}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>
                          </React.Fragment>
                          );
                        })
                      ) : (
                        <Grid item mt={3} xs={12} className="view_box_list">
                          <Typography
                            className="detail_title mn_hdng"
                            component="h2"
                            mb={3}
                          >
                            {translate?.main?.PlanDetails}
                          </Typography>
                          <Grid item lg={3} md={3} sm={6} xs={12}>
                            <Box>
                              <Typography component="h5">
                                {translate?.main?.noactivesubscription}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      )}

                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                    justifyContent: "left",
                    minHeight: 320,
                    py: 2,
                  }}
                >
                  {licenseUrl ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                        maxWidth: 360,
                      }}
                    >
                      <Box
                        className="upload_image_preview2"
                        onClick={handleViewLicense}
                        sx={{
                          width: "100%",
                          maxWidth: 280,
                          height: 220,
                          cursor: "pointer",
                          overflow: "hidden",
                          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                          "&:hover": {
                            borderColor: "primary.main",
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={licensePreview}
                          alt="license document"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            p: 2,
                            pointerEvents: "none",
                          }}
                        />
                      </Box>
                      <Button
                        className="btn btn_primary"
                        startIcon={<VisibilityIcon />}
                        onClick={handleViewLicense}
                      >
                        {translate?.agents?.viewLicense}
                      </Button>

                      {/* Accept / Reject buttons — only shown when document is pending (1) */}
                      {useDetails?.documentStatus === 1 && (
                        <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
                          <Button
                            variant="contained"
                            color="success"
                            disabled={isSubmitting}
                            onClick={() => handleDocumentStatus(2)}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            disabled={isSubmitting}
                            onClick={() => setRejectDialogOpen(true)}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}

                      {/* Already-accepted / rejected badge */}
                      {useDetails?.documentStatus === 2 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ color: "success.main", fontWeight: 600 }}>
                            ✅ Document Accepted
                          </Typography>
                        </Box>
                      )}
                      {useDetails?.documentStatus === 3 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ color: "error.main", fontWeight: 600 }}>
                            ❌ Document Rejected
                          </Typography>
                          {useDetails?.userRejectedReason && (
                            <Typography variant="body2" color="text.secondary">
                              Reason: {useDetails.userRejectedReason}
                            </Typography>
                          )}
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            sx={{ mt: 1 }}
                            disabled={isSubmitting}
                            onClick={() => handleDocumentStatus(2)}
                          >
                            Accept Anyway
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        color: "text.secondary",
                        textAlign: "center",
                      }}
                    >
                      <DescriptionOutlinedIcon
                        sx={{ fontSize: 64, opacity: 0.35 }}
                      />
                      <Typography variant="body1">
                        {translate?.agents?.noLicenseDocument}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </CustomTabPanel>
          </Box>
        </Card>

        <Card sx={{ mt: 4 }} className="cards">
          <CardContent>
            <Typography className="detail_title mn_hdng" component="h2" mb={3}>
              Subscription Plan History
            </Typography>
            <TableContainer className="table_container">
              <Table sx={{ minWidth: 1000 }} aria-label="agent subscription history">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Purchased</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Credits Granted</TableCell>
                    <TableCell>Credits Used</TableCell>
                    <TableCell>Credits Remaining</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {useDetails?.subscriptionHistory?.length ? (
                    useDetails.subscriptionHistory.map((row: any, index: number) => {
                      const req = creditBreakdown(row?.acceptRequests, row?.acceptRequestsLeft);
                      const nor = creditBreakdown(row?.normalCredit, row?.normalCreditLeft);
                      const hig = creditBreakdown(row?.highlightCredit, row?.highlightCreditLeft);
                      return (
                      <TableRow key={row?._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {row?.planName || row?.membershipPlan?.planName || "-"}
                        </TableCell>
                        <TableCell>
                          {Number.isFinite(Number(row?.price))
                            ? `${Number(row.price).toLocaleString("en-IN")} KD`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row?.createdAt
                            ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row?.expiryDate
                            ? moment(row.expiryDate).format("DD MMM YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={subscriptionStatusLabel(row?.status)}
                            color={subscriptionStatusColor(row?.status)}
                          />
                        </TableCell>
                        <TableCell>
                          Normal: {nor.planTotal}
                          {nor.carried > 0 ? ` (+${nor.carried} carried)` : ""}
                          <br />
                          Highlight: {hig.planTotal}
                          {hig.carried > 0 ? ` (+${hig.carried} carried)` : ""}
                          <br />
                          Requests: {req.planTotal}
                          {req.carried > 0 ? ` (+${req.carried} carried)` : ""}
                        </TableCell>
                        <TableCell>
                          Normal: {nor.used}
                          <br />
                          Highlight: {hig.used}
                          <br />
                          Requests: {req.used}
                        </TableCell>
                        <TableCell>
                          Normal: {nor.remaining}
                          <br />
                          Highlight: {hig.remaining}
                          <br />
                          Requests: {req.remaining}
                        </TableCell>
                        <TableCell>{row?.transactionId || "-"}</TableCell>
                        <TableCell align="center">
                          {Number(row?.status) === 1 ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                disabled={!row?._id || planActionLoading}
                                onClick={() =>
                                  setPlanActionDialog({
                                    open: true,
                                    action: "expire",
                                    plan: row,
                                  })
                                }
                              >
                                Expire
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={!row?._id || planActionLoading}
                                onClick={() =>
                                  setPlanActionDialog({
                                    open: true,
                                    action: "cancel",
                                    plan: row,
                                  })
                                }
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <NoDataFound message="No subscription history found" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card sx={{ mt: 4 }} className="cards">
          <CardContent>
            <Typography className="detail_title mn_hdng" component="h2" mb={3}>
              Payment History
            </Typography>
            <TableContainer className="table_container">
              <Table sx={{ minWidth: 1100 }} aria-label="agent payment history">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Payment For</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Provider Charge ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {useDetails?.paymentHistory?.length ? (
                    useDetails.paymentHistory.map((row: any, index: number) => {
                      const details =
                        Number(row?.paymentType) === 1
                          ? `Normal: ${row?.normalCreditsBought ?? 0}, Highlight: ${
                              row?.highlightCreditsBought ?? 0
                            }`
                          : Number(row?.paymentType) === 2
                            ? row?.subscription?.planName || "Subscription plan"
                            : row?.property?.title ||
                              row?.property?.propertyName ||
                              "Property request";

                      return (
                        <TableRow key={row?._id || index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{paymentTypeLabel(row?.paymentType)}</TableCell>
                          <TableCell>{details}</TableCell>
                          <TableCell>
                            {Number.isFinite(Number(row?.amount))
                              ? `${Number(row.amount).toLocaleString("en-IN")} ${
                                  row?.currency || "KWD"
                                }`
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {row?.createdAt
                              ? moment(row.createdAt).format(
                                  "DD MMM YYYY, hh:mm A",
                                )
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={paymentStatusLabel(row?.paymentStatus)}
                              color={
                                Number(row?.paymentStatus) === 2
                                  ? "success"
                                  : Number(row?.paymentStatus) === 4
                                    ? "info"
                                    : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>{row?.paymentMode || "-"}</TableCell>
                          <TableCell>{row?.transactionId || "-"}</TableCell>
                          <TableCell>{row?.providerChargeId || "-"}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <NoDataFound message="No payment history found" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <DemoToolsPanel
          variant="user"
          entityId={id}
          context={{
            role: useDetails?.role,
            membershipBought: useDetails?.membershipBought,
            walletNormal: useDetails?.normalCred ?? 0,
            walletHighlight: useDetails?.highlightCred ?? 0,
            planNormalLeft: totalNormalCreditsLeft,
            planHighlightLeft: totalHighlightCreditsLeft,
            planAcceptLeft: totalRequestsLeft,
            planExpiry: (() => {
              const active = subscriptionPlans.find(
                (p: any) =>
                  p?.expiryDate && new Date(p.expiryDate).getTime() > Date.now(),
              );
              const expiry = active?.expiryDate || subscriptionPlans[0]?.expiryDate;
              return expiry
                ? moment(expiry).format("DD MMM YYYY, hh:mm A")
                : "-";
            })(),
            creditExpiry: (() => {
              const active = subscriptionPlans.find(
                (p: any) =>
                  p?.expiryDate && new Date(p.expiryDate).getTime() > Date.now(),
              );
              const expiry =
                active?.creditExpiryDate ||
                active?.expiryDate ||
                subscriptionPlans[0]?.creditExpiryDate;
              return expiry
                ? moment(expiry).format("DD MMM YYYY, hh:mm A")
                : "-";
            })(),
            documentStatus: useDetails?.documentStatus,
          }}
          onSuccess={() => {
            if (id) getUserDetailById(id);
          }}
        />
      </div>

      <Dialog
        open={licensePreviewOpen}
        onClose={() => setLicensePreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", md: "space-between" },
            px: 2,
            pt: 2,
          }}
        >
          <Typography variant="h6">
            {translate?.agents?.licenseDocuments}
          </Typography>
          <IconButton
            onClick={() => setLicensePreviewOpen(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {isPdfUrl(licenseUrl) ? (
            <Box
              component="iframe"
              src={licenseUrl}
              title="license document"
              sx={{
                width: "100%",
                height: "75vh",
                border: "none",
                borderRadius: 1,
                bgcolor: "#f5f5f5",
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxHeight: "75vh",
                overflow: "auto",
              }}
            >
              <Box
                component="img"
                src={licenseUrl}
                alt="license document"
                sx={{
                  maxWidth: "100%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Reason Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => { setRejectDialogOpen(false); setRejectReason(""); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please enter a reason for rejecting this document. The agent will be notified.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Document is blurry or invalid"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => { setRejectDialogOpen(false); setRejectReason(""); }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isSubmitting}
            onClick={() => handleDocumentStatus(3, rejectReason)}
          >
            {isSubmitting ? "Rejecting..." : "Confirm Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={planActionDialog.open}
        onClose={() => {
          if (!planActionLoading) {
            setPlanActionDialog({ open: false, action: null, plan: null });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {planActionDialog.action === "expire" ? "Expire Plan" : "Cancel Plan"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {planActionDialog.action === "expire"
              ? `Expire "${planActionDialog.plan?.planName || "this plan"}"? Remaining credits on this plan will move to the next active plan and stay valid until that plan ends.`
              : `Cancel "${planActionDialog.plan?.planName || "this plan"}"? Remaining credits on this plan will move to the next active plan and stay valid until that plan ends.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            disabled={planActionLoading}
            onClick={() =>
              setPlanActionDialog({ open: false, action: null, plan: null })
            }
          >
            Close
          </Button>
          <Button
            variant="contained"
            color={planActionDialog.action === "expire" ? "warning" : "error"}
            disabled={planActionLoading}
            onClick={handlePlanActionConfirm}
          >
            {planActionLoading
              ? "Processing..."
              : planActionDialog.action === "expire"
                ? "Confirm Expire"
                : "Confirm Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default AgentDetails;
